"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { toast } from "sonner"
import SecondarySideBar from "@/components/shared/SecondarySideBar"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { ChevronDown, ChevronRight, Loader2, X, ClipboardCopy, Check, Pencil, RotateCcw, Download, Trash2 } from "lucide-react"
import { useExtractions } from "./ExtractionsContext"
import { useExtractionPolling } from "@/hooks/useExtractionPolling"
import { useAuth } from "@/hooks/useAuth"
import { removeFromExtraction, createExtraction, updateVariableRecodes, clearExtraction } from "@/services/extractionsService"
import type { ExtractionVariable, SurveyInfoRecord } from "@/services/extractionsService"
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip"
import { buildSurveyName, getUnitOfAnalysisLabel } from "@/utils/surveyUtils"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import LoadingSpinner from "@/components/shared/LoadingSpinner"

// ---------------------------------------------------------------------------
// Collapse state — in-memory, scoped per session
// ---------------------------------------------------------------------------

/**
 * In-memory store of explicit open/closed state for collapsible nodes, keyed
 * by session ID. Only nodes that have been toggled (or whose state differs
 * from their default) are stored. Resets when the session changes.
 */
const collapseStoreBySession = new Map<string, Map<string, boolean>>()

/**
 * Gets the state map for a given session, creating an empty one if needed.
 *
 * @param sessionId - The chat session UUID.
 * @returns The mutable Map of nodeKey → isOpen state.
 */
function getCollapseMap(sessionId: string): Map<string, boolean> {
  let map = collapseStoreBySession.get(sessionId)
  if (!map) {
    map = new Map<string, boolean>()
    collapseStoreBySession.set(sessionId, map)
  }
  return map
}

/**
 * Removes a node key from the collapse store for a session.
 *
 * @param sessionId - The chat session UUID.
 * @param nodeKey - The node key to remove (e.g. "var:KE2020DHS#file.dta#hv105").
 */
function removeFromCollapseStore(sessionId: string, nodeKey: string): void {
  const map = collapseStoreBySession.get(sessionId)
  if (map) {
    map.delete(nodeKey)
  }
}

/**
 * Clears the entire collapse store for a session. Called when the user
 * switches to a different session so state starts fresh.
 *
 * @param sessionId - The chat session UUID to clear.
 */
function clearCollapseStore(sessionId: string): void {
  collapseStoreBySession.delete(sessionId)
}

/**
 * Hook that provides in-memory open/closed state for a collapsible node in
 * the extraction panel. State survives re-renders and data refreshes but
 * resets when the user switches sessions.
 *
 * @param sessionId - The chat session UUID (scopes state per session).
 * @param nodeKey - A unique identifier for the collapsible node.
 * @param defaultOpen - Whether the node should default to open if no stored state exists.
 * @returns A tuple of [isOpen, setIsOpen] similar to useState.
 */
function usePersistedCollapse(
  sessionId: string,
  nodeKey: string,
  defaultOpen: boolean
): [boolean, (open: boolean) => void] {
  const [isOpen, setIsOpenInternal] = useState<boolean>(() => {
    const map = getCollapseMap(sessionId)
    const stored = map.get(nodeKey)
    return stored !== undefined ? stored : defaultOpen
  })

  const sessionIdRef = useRef<string>(sessionId)
  sessionIdRef.current = sessionId

  const nodeKeyRef = useRef<string>(nodeKey)
  nodeKeyRef.current = nodeKey

  const setIsOpen = useCallback((open: boolean): void => {
    setIsOpenInternal(open)
    const map = getCollapseMap(sessionIdRef.current)
    map.set(nodeKeyRef.current, open)
  }, [])

  return [isOpen, setIsOpen]
}

interface ExtractionsSidebarProps {
  isOpen: boolean
  onToggle: () => void
  sessionId: string
}

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------

/**
 * Represents the hierarchical grouping of extraction variables:
 * Survey → File → Variables (sorted alphabetically by variable name).
 */
interface SurveyGroup {
  surveyId: string
  programName: string
  surveyName: string
  files: Map<string, ExtractionVariable[]>
}

/**
 * Groups extraction variables hierarchically by survey ID, then by file name.
 * Variables within each file group are sorted alphabetically by variable name.
 * File names are sorted alphabetically within each survey group. Survey groups
 * are sorted alphabetically by survey ID.
 *
 * @param variables - The flat list of extraction variables from the backend.
 * @returns An array of SurveyGroup objects representing the hierarchy.
 */
function groupVariablesBySurveyAndFile(variables: ExtractionVariable[], surveyInfoMap: Map<string, SurveyInfoRecord>): SurveyGroup[] {
  // Build a map of surveyId → SurveyGroup
  const surveyMap = new Map<string, SurveyGroup>()

  for (const variable of variables) {
    let group: SurveyGroup | undefined = surveyMap.get(variable.surveyId)
    if (!group) {
      const info = surveyInfoMap.get(variable.surveyId)
      const hasSurveyMetadata: boolean = Boolean(info?.countryName && info?.surveyYear)
      const displayName: string = hasSurveyMetadata
        ? buildSurveyName(
            info!.programName,
            info!.countryName,
            info!.surveyYear,
            info!.round
          )
        : variable.surveyId

      group = {
        surveyId: variable.surveyId,
        programName: variable.programName,
        surveyName: displayName,
        files: new Map<string, ExtractionVariable[]>(),
      }
      surveyMap.set(variable.surveyId, group)
    }

    // Group variable under its file name
    const fileName: string = variable.fileName
    const fileVariables: ExtractionVariable[] = group.files.get(fileName) ?? []
    fileVariables.push(variable)
    group.files.set(fileName, fileVariables)
  }

  // Sort variables within each file alphabetically by variableName,
  // and sort file names alphabetically within each survey group.
  for (const group of surveyMap.values()) {
    for (const [fileName, fileVars] of group.files.entries()) {
      group.files.set(
        fileName,
        fileVars.sort((a, b) => a.variableName.localeCompare(b.variableName))
      )
    }

    // Rebuild the files map sorted alphabetically by file name
    group.files = new Map<string, ExtractionVariable[]>(
      Array.from(group.files.entries()).sort(([a], [b]) => a.localeCompare(b))
    )
  }

  // Return groups sorted alphabetically by surveyName
  return Array.from(surveyMap.values()).sort((a, b) =>
    a.surveyName.localeCompare(b.surveyName)
  )
}

/**
 * Represents the JSON structure for a single variable in the exported extraction.
 */
interface ExportedVariable {
  variableName: string
  variableLabel: string
  variableLabelRename?: string
  valueRecode?: Record<string, string>
  values?: Array<{ value: string; valueDescription: string }>
}

/**
 * Represents the JSON structure for a single file in the exported extraction.
 */
interface ExportedFile {
  sourceFileName: string
  outputFileName: string
  unitOfAnalysis?: string
  variables: ExportedVariable[]
}

/**
 * Represents the JSON structure for a single survey in the exported extraction.
 */
interface ExportedSurvey {
  surveyId: string
  surveyName: string
  programName: string
  countryName?: string
  surveyYear?: string
  round?: string
  files: ExportedFile[]
}

/**
 * Top-level structure of the exported extraction JSON.
 */
interface ExportedExtraction {
  sessionId: string
  exportedAt: string
  extractionExists: boolean
  extractionS3Prefix: string
  surveys: ExportedSurvey[]
}

/**
 * Builds a JSON-serializable representation of the current extraction state
 * and triggers a browser file download of the result.
 *
 * The exported JSON includes all surveys, files, variables, labels, recodes,
 * output file names, and survey metadata — everything needed to reconstruct
 * or audit the extraction configuration.
 *
 * @param sessionId - The chat session UUID this extraction belongs to.
 * @param surveyGroups - The hierarchically grouped variables (Survey → File → Variable).
 * @param extractionExists - Whether an extraction has been created for this session.
 * @param extractionS3Prefix - The S3 prefix where the extraction output lives.
 * @param outputFileNames - Map of file keys to user-specified output CSV names.
 * @param surveyInfoMap - Map of surveyId → survey metadata records.
 * @param fileUnitOfAnalysis - Map of file keys → unit of analysis codes.
 */
function exportExtractionAsJson({
  sessionId,
  surveyGroups,
  extractionExists,
  extractionS3Prefix,
  outputFileNames,
  surveyInfoMap,
  fileUnitOfAnalysis,
}: {
  sessionId: string
  surveyGroups: SurveyGroup[]
  extractionExists: boolean
  extractionS3Prefix: string
  outputFileNames: Map<string, string>
  surveyInfoMap: Map<string, SurveyInfoRecord>
  fileUnitOfAnalysis: Map<string, string>
}): void {
  const exportData: ExportedExtraction = {
    sessionId,
    exportedAt: new Date().toISOString(),
    extractionExists,
    extractionS3Prefix,
    surveys: surveyGroups.map((group): ExportedSurvey => {
      const info = surveyInfoMap.get(group.surveyId)
      return {
        surveyId: group.surveyId,
        surveyName: group.surveyName,
        programName: group.programName,
        ...(info?.countryName && { countryName: info.countryName }),
        ...(info?.surveyYear && { surveyYear: info.surveyYear }),
        ...(info?.round && { round: info.round }),
        files: Array.from(group.files.entries()).map(([fileName, variables]): ExportedFile => {
          const fileKey = `${group.surveyId}/${fileName}`
          const defaultOutputName = fileName.lastIndexOf(".") > 0
            ? `${fileName.substring(0, fileName.lastIndexOf("."))}.csv`
            : `${fileName}.csv`
          const outputName = outputFileNames.get(fileKey) ?? defaultOutputName
          const unitOfAnalysis = fileUnitOfAnalysis.get(fileKey)

          return {
            sourceFileName: fileName,
            outputFileName: outputName,
            ...(unitOfAnalysis && { unitOfAnalysis }),
            variables: variables.map((v): ExportedVariable => ({
              variableName: v.variableName,
              variableLabel: v.variableLabel,
              ...(v.variableLabelRename && { variableLabelRename: v.variableLabelRename }),
              ...(v.valueRecode && Object.keys(v.valueRecode).length > 0 && { valueRecode: v.valueRecode }),
              ...(v.values && v.values.length > 0 && { values: v.values }),
            })),
          }
        }),
      }
    }),
  }

  // Serialize and trigger browser download
  const jsonString: string = JSON.stringify(exportData, null, 2)
  const blob = new Blob([jsonString], { type: "application/json" })
  const url: string = URL.createObjectURL(blob)

  const anchor: HTMLAnchorElement = document.createElement("a")
  anchor.href = url
  anchor.download = `extraction-${sessionId.slice(0, 8)}.json`
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

/**
 * Combobox input for recode values. Combines a free-text textarea with a
 * dropdown of previously entered recode values for the same variable,
 * allowing the user to type a new value or select an existing one.
 *
 * @param value - The current recode value for this row.
 * @param onChange - Callback when the value changes (typed or selected).
 * @param suggestions - Unique previously entered recode values to suggest.
 * @param ariaLabel - Accessible label for the input.
 * @returns The rendered combobox element.
 */
function RecodeCombobox({
  value,
  onChange,
  suggestions,
  ariaLabel,
}: {
  value: string
  onChange: (value: string) => void
  suggestions: string[]
  ariaLabel: string
}): React.JSX.Element {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const popoverRef = useRef<HTMLDivElement>(null)

  const filtered = suggestions.filter(
    (s) => s !== value && s.toLowerCase().includes(value.toLowerCase())
  )

  /**
   * Only closes the popover if focus moved outside the dropdown.
   */
  function handleBlur(e: React.FocusEvent): void {
    if (popoverRef.current?.contains(e.relatedTarget as Node | null)) return
    setTimeout(() => setIsOpen(false), 150)
  }

  return (
    <Popover open={isOpen && filtered.length > 0} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild onClick={(e) => e.preventDefault()}>
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onBlur={handleBlur}
          placeholder="—"
          className="min-h-7 py-1 text-sm w-full resize-none field-sizing-normal"
          rows={1}
          aria-label={ariaLabel}
        />
      </PopoverTrigger>
      <PopoverContent
        ref={popoverRef}
        align="start"
        className="w-[var(--radix-popover-trigger-width)] p-0 gap-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="max-h-48 overflow-y-auto p-1" onWheel={(e) => e.stopPropagation()}>
          {filtered.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-muted/50 cursor-pointer whitespace-normal break-words"
              onMouseDown={(e) => {
                e.preventDefault()
                onChange(suggestion)
                setIsOpen(false)
              }}
            >
              {suggestion}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Dialog for viewing and editing a variable's details.
 * Displays the survey ID, output file name, variable name, and an editable
 * label at the top, followed by a table of all value/description pairs with
 * an editable "Recode" combobox column. The label is editable via an inline
 * pencil button with a reset option. Can only be closed via "Apply" or
 * "Cancel" buttons (no outside-click dismiss, no close X button).
 *
 * When the user clicks "Apply", any changes to the label rename or value
 * recodes are sent to the backend in a single PATCH request.
 *
 * @param variable - The extraction variable to display.
 * @param outputFileName - The resolved output CSV file name (renamed if customized).
 * @param surveyId - The survey identifier (e.g. "KE2020DHS").
 * @param onClose - Callback invoked when the user clicks Apply or Cancel.
 * @returns The rendered dialog element.
 */
function EditVariableDialog({
  variable,
  outputFileName,
  surveyId,
  onClose,
}: {
  variable: ExtractionVariable
  outputFileName: string
  surveyId: string
  onClose: () => void
}): React.JSX.Element {
  const values = variable.values ?? []
  const auth = useAuth()
  const { refresh, surveyInfo: surveyInfoMap, fileUnitOfAnalysis } = useExtractions()

  // The effective label: use variableLabelRename if set, otherwise variableLabel
  const effectiveLabel: string = variable.variableLabelRename || variable.variableLabel

  const [isEditingLabel, setIsEditingLabel] = useState<boolean>(false)
  const [labelEditValue, setLabelEditValue] = useState<string>(effectiveLabel)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const labelInputRef = useRef<HTMLInputElement>(null)

  // Local state for value recodes: a map from value → recoded description.
  // Initialized from the variable's existing valueRecode (or empty).
  const [recodeMap, setRecodeMap] = useState<Record<string, string>>(
    () => variable.valueRecode ?? {}
  )

  // Unique non-empty recode values entered so far, used as suggestions in the combobox.
  const recodeSuggestions: string[] = useMemo(
    () => Array.from(new Set(Object.values(recodeMap).filter(Boolean))),
    [recodeMap]
  )

  // Focus the label input when editing starts
  useEffect(() => {
    if (isEditingLabel) {
      labelInputRef.current?.focus()
      labelInputRef.current?.select()
    }
  }, [isEditingLabel])

  /**
   * Updates the local recode map for a single value.
   *
   * @param value - The original value key.
   * @param recode - The new recoded description (empty string removes the recode).
   */
  function handleRecodeChange(value: string, recode: string): void {
    setRecodeMap((prev) => {
      const next = { ...prev }
      if (recode) {
        next[value] = recode
      } else {
        delete next[value]
      }
      return next
    })
  }

  /**
   * Handles the "Apply" button click.
   * Compares current state against the variable's persisted values and sends
   * a PATCH request with any changes. Then closes the dialog.
   * If the label was cleared, it reverts to the effective label (no-op).
   */
  async function handleApply(): Promise<void> {
    // If the user cleared the label, revert to the effective label
    const trimmedLabel = labelEditValue.trim() || effectiveLabel
    const labelChanged = trimmedLabel !== effectiveLabel

    // Determine if recodes changed by comparing with the original
    const originalRecode = variable.valueRecode ?? {}
    const recodeChanged =
      JSON.stringify(recodeMap) !== JSON.stringify(originalRecode)

    if (labelChanged || recodeChanged) {
      setIsSaving(true)
      try {
        const accessToken: string | null = await auth.getAccessToken()
        if (!accessToken) {
          throw new Error("Authentication required.")
        }

        // Build the payload — only include fields that changed
        const payload: {
          sessionId: string
          surveyId: string
          fileName: string
          variableName: string
          variableLabelRename?: string
          valueRecode?: Record<string, string>
        } = {
          sessionId: variable.sessionId,
          surveyId: variable.surveyId,
          fileName: variable.fileName,
          variableName: variable.variableName,
        }

        if (labelChanged) {
          payload.variableLabelRename =
            trimmedLabel === variable.variableLabel ? "" : trimmedLabel
        }

        if (recodeChanged) {
          payload.valueRecode = recodeMap
        }

        await updateVariableRecodes(
          payload,
          accessToken
        )

        await refresh()
      } catch (err) {
        console.error("Failed to update variable recodes:", err)
      } finally {
        setIsSaving(false)
      }
    }

    onClose()
  }

  /**
   * Handles the "Cancel" button click.
   * Discards any unsaved changes and closes the dialog.
   */
  function handleCancel(): void {
    setLabelEditValue(effectiveLabel)
    setIsEditingLabel(false)
    setRecodeMap(variable.valueRecode ?? {})
    onClose()
  }

  return (
    <Dialog open={true} onOpenChange={() => { /* Prevent closing via overlay or escape */ }}>
      <DialogContent
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        className="sm:max-w-2xl max-h-[80vh] flex flex-col"
      >
        <DialogHeader>
          <DialogTitle>{variable.variableName}</DialogTitle>
          <p className="text-xs text-muted-foreground">
            Survey name: {(() => {
              const info = surveyInfoMap.get(surveyId)
              return info?.countryName && info?.surveyYear
                ? `${buildSurveyName(info.programName, info.countryName, info.surveyYear, info.round)} (${surveyId})`
                : surveyId
            })()}
          </p>
          <p className="text-xs text-muted-foreground">Output file name: {outputFileName}</p>
          {(() => {
            const fileKey = `${surveyId}/${variable.fileName}`
            const unitCode = fileUnitOfAnalysis.get(fileKey) ?? ""
            const pName = surveyInfoMap.get(surveyId)?.programName ?? variable.programName
            const label = getUnitOfAnalysisLabel(unitCode, pName)
            return label ? (
              <p className="text-xs text-muted-foreground">Unit of analysis: {label}</p>
            ) : null
          })()}
          <DialogDescription asChild>
            <div className="flex items-center gap-2">
              Output column name:
              {isEditingLabel ? (
                <Input
                  ref={labelInputRef}
                  value={labelEditValue}
                  onChange={(e) => setLabelEditValue(e.target.value)}
                  onBlur={() => {
                    if (!labelEditValue.trim()) setLabelEditValue(effectiveLabel)
                    setIsEditingLabel(false)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (!labelEditValue.trim()) setLabelEditValue(effectiveLabel)
                      setIsEditingLabel(false)
                    }
                    if (e.key === "Escape") {
                      setLabelEditValue(effectiveLabel)
                      setIsEditingLabel(false)
                    }
                  }}
                  className="h-7 text-sm"
                  aria-label="Variable label"
                />
              ) : (
                <span className="text-sm text-muted-foreground">{labelEditValue}</span>
              )}
              <button
                onClick={() => setIsEditingLabel(!isEditingLabel)}
                className="shrink-0 p-0.5 rounded hover:bg-muted/50 transition-colors"
                aria-label="Edit variable label"
              >
                <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
              {labelEditValue !== variable.variableLabel && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => {
                        setLabelEditValue(variable.variableLabel)
                        setIsEditingLabel(false)
                      }}
                      className="shrink-0 p-0.5 rounded hover:bg-muted/50 transition-colors"
                      aria-label="Reset label to default"
                    >
                      <RotateCcw className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top">Reset to default</TooltipContent>
                </Tooltip>
              )}
            </div>
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable table of values with recode column */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden border rounded-md">
          {values.length > 0 ? (
            <Table className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">Category</TableHead>
                  <TableHead className="w-[40%]">Description</TableHead>
                  <TableHead>Recode</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {values.map((val, i) => (
                  <TableRow key={`${val.value}-${i}`}>
                    <TableCell className="font-mono align-top">{val.value}</TableCell>
                    <TableCell className="whitespace-normal align-top">{val.valueDescription}</TableCell>
                    <TableCell className="align-top">
                      <RecodeCombobox
                        value={recodeMap[val.value] ?? ""}
                        onChange={(newValue) => handleRecodeChange(val.value, newValue)}
                        suggestions={recodeSuggestions}
                        ariaLabel={`Recode for value ${val.value}`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="p-4 text-sm text-muted-foreground text-center">
              No category values available for this variable. It is likely numerical or non-categorical.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleApply} disabled={isSaving}>
            {isSaving ? (
              <LoadingSpinner loadingContent="Saving..." />
              ) : (
              "Apply"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Collapsible section for a single variable within a file group.
 * Shows the variable name and label as the trigger, with the value/description
 * pairs listed underneath when expanded. Includes an edit button (pencil icon)
 * to open a detail dialog, and a remove button (X) to delete the variable
 * from the extraction.
 *
 * @param variable - The extraction variable record.
 * @param sessionId - The chat session UUID for persisting collapse state.
 * @param outputFileName - The resolved output CSV file name (renamed if customized).
 * @param surveyId - The survey identifier (e.g. "KE2020DHS").
 * @returns A collapsible variable section element.
 */
function VariableSection({ variable, sessionId, outputFileName, surveyId }: { variable: ExtractionVariable; sessionId: string; outputFileName: string; surveyId: string }): React.JSX.Element {
  const nodeKey = `var:${variable.sortKey}`
  const [isOpen, setIsOpen] = usePersistedCollapse(sessionId, nodeKey, false)
  const [isRemoving, setIsRemoving] = useState<boolean>(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false)
  const auth = useAuth()
  const { refresh } = useExtractions()
  const values = variable.values ?? []

  /**
   * Handles removing this variable from the extraction.
   * Calls the backend DELETE endpoint and refreshes the shared context.
   *
   * @param e - The mouse event (stopped to prevent triggering the collapsible).
   */
  async function handleRemove(e: React.MouseEvent): Promise<void> {
    e.stopPropagation()
    if (isRemoving) return

    setIsRemoving(true)
    try {
      const accessToken: string | null = await auth.getAccessToken()
      if (!accessToken) {
        throw new Error("Authentication required.")
      }

      await removeFromExtraction(
        {
          sessionId: variable.sessionId,
          surveyId: variable.surveyId,
          fileName: variable.fileName,
          variableName: variable.variableName,
        },
        accessToken
      )

      // Clean up this variable's collapse state from the in-memory store.
      removeFromCollapseStore(variable.sessionId, `var:${variable.sortKey}`)

      await refresh()
    } catch (err) {
      console.error("Failed to remove variable from extraction:", err)
    } finally {
      setIsRemoving(false)
    }
  }

  /**
   * Opens the edit variable dialog.
   * Stops event propagation to prevent toggling the collapsible section.
   *
   * @param e - The mouse event.
   */
  function handleEditClick(e: React.MouseEvent): void {
    e.stopPropagation()
    setIsEditDialogOpen(true)
  }

  return (
    <>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="flex items-center group">
          <CollapsibleTrigger className="flex items-center gap-1 flex-1 min-w-0 text-left cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5">
            {values.length > 0 ? (
              isOpen ? (
                <ChevronDown className="h-2.5 w-2.5 shrink-0 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-2.5 w-2.5 shrink-0 text-muted-foreground" />
              )
            ) : (
              <span className="w-2.5 shrink-0" />
            )}
            <span className="text-[11px] text-foreground leading-tight">
              {variable.variableName}
              <span className="text-muted-foreground ml-1">— {variable.variableLabelRename || variable.variableLabel}</span>
            </span>
          </CollapsibleTrigger>
          <div className="flex items-center shrink-0">
            <button
              onClick={handleEditClick}
              className="p-0.5 rounded hover:bg-muted/50 transition-colors"
              aria-label={`Edit ${variable.variableName}`}
            >
              <Pencil className="h-2.5 w-2.5 text-muted-foreground" />
            </button>
            <span className="w-[18px] flex justify-center">
              <button
                onClick={handleRemove}
                disabled={isRemoving}
                className="p-0.5 rounded hover:bg-destructive/10 transition-colors"
                aria-label={`Remove ${variable.variableName} from extraction`}
              >
                {isRemoving ? (
                  <Loader2 className="h-2.5 w-2.5 animate-spin text-muted-foreground" />
                ) : (
                  <X className="h-2.5 w-2.5 text-destructive/60" />
                )}
              </button>
            </span>
          </div>
        </div>
        {values.length > 0 && (
          <CollapsibleContent>
            <div className="pl-4 mt-0.5 overflow-x-hidden">
              <table className="w-full text-[10px] text-muted-foreground">
                <thead>
                  <tr>
                    <th className="text-left font-medium pr-2">Category</th>
                    <th className="text-left font-medium pr-2">Description</th>
                    {variable.valueRecode && Object.keys(variable.valueRecode).length > 0 && (
                      <th className="text-left font-medium">Recode</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {values.map((val, i) => (
                    <tr key={`${val.value}-${i}`} className="leading-snug border-t border-border/50">
                      <td className="font-mono pr-2 align-top">{val.value}</td>
                      <td className="pr-2 align-top">{val.valueDescription}</td>
                      {variable.valueRecode && Object.keys(variable.valueRecode).length > 0 && (
                        <td className="align-top break-words max-w-[80px]">{variable.valueRecode[val.value] || ""}</td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CollapsibleContent>
        )}
      </Collapsible>

      {/* Edit variable detail dialog — conditionally rendered so it remounts fresh each time */}
      {isEditDialogOpen && (
        <EditVariableDialog
          variable={variable}
          outputFileName={outputFileName}
          surveyId={surveyId}
          onClose={() => setIsEditDialogOpen(false)}
        />
      )}
    </>
  )
}

/**
 * Derives a default output CSV file name from a source data file name.
 * Strips the original extension and appends ".csv".
 * If the source has no extension, appends ".csv".
 *
 * @param sourceFileName - The original data file name (e.g. "KEHR7AFL.SAV").
 * @returns The default output name (e.g. "KEHR7AFL.csv").
 */
function deriveDefaultOutputName(sourceFileName: string): string {
  const lastDot = sourceFileName.lastIndexOf(".")
  if (lastDot > 0) {
    return `${sourceFileName.substring(0, lastDot)}.csv`
  }
  return `${sourceFileName}.csv`
}

/**
 * Collapsible section for a single file within a survey group.
 * The collapsible trigger displays the output CSV file name (editable inline)
 * instead of the raw source file name. Defaults to the source name with a
 * .csv extension. An edit (pencil) button toggles inline editing, and a reset
 * button restores the default name. The output name is persisted to DynamoDB
 * via the ExtractionsContext.
 *
 * @param fileName - The source data file name (e.g. "KEHR7AFL.SAV").
 * @param variables - The variables sourced from this file, pre-sorted.
 * @param sessionId - The chat session UUID for persisting collapse state.
 * @param surveyId - The survey identifier (used to build a unique node key).
 * @returns A collapsible file section element.
 */
function FileSection({
  fileName,
  variables,
  sessionId,
  surveyId,
}: {
  fileName: string
  variables: ExtractionVariable[]
  sessionId: string
  surveyId: string
}): React.JSX.Element {
  const nodeKey = `file:${surveyId}/${fileName}`
  const [isOpen, setIsOpen] = usePersistedCollapse(sessionId, nodeKey, true)
  const { outputFileNames, setOutputFileName, fileUnitOfAnalysis, surveyInfo: surveyInfoMap } = useExtractions()

  const fileKey = `${surveyId}/${fileName}`
  const defaultOutputName = deriveDefaultOutputName(fileName)
  const currentOutputName = outputFileNames.get(fileKey) ?? defaultOutputName
  const isCustomName = currentOutputName !== defaultOutputName

  // Resolve unit of analysis label for display in brackets
  const unitCode: string = fileUnitOfAnalysis.get(fileKey) ?? ""
  const programName: string = surveyInfoMap.get(surveyId)?.programName ?? variables[0]?.programName ?? ""
  const unitLabel: string = getUnitOfAnalysisLabel(unitCode, programName)

  const [isEditing, setIsEditing] = useState<boolean>(false)
  const [editValue, setEditValue] = useState<string>(currentOutputName)
  const [duplicateError, setDuplicateError] = useState<boolean>(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Sync editValue when the stored output name changes externally (e.g. on load)
  useEffect(() => {
    if (!isEditing) {
      setEditValue(currentOutputName)
    }
  }, [currentOutputName, isEditing])

  // Focus the input when editing starts
  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [isEditing])

  /**
   * Begins inline editing of the output file name.
   * Stops event propagation to prevent the collapsible trigger from toggling.
   *
   * @param e - The mouse event.
   */
  function handleEditClick(e: React.MouseEvent): void {
    e.stopPropagation()
    setIsEditing(true)
  }

  /**
   * Resets the output file name back to the default derived from the source.
   * Stops event propagation to prevent the collapsible trigger from toggling.
   *
   * @param e - The mouse event.
   */
  function handleReset(e: React.MouseEvent): void {
    e.stopPropagation()
    setEditValue(defaultOutputName)
    setOutputFileName(fileKey, defaultOutputName)
    setIsEditing(false)
  }

  /**
   * Commits the edited output file name. Ensures the name ends with ".csv"
   * (lowercase) and is not empty. If the user typed an uppercase variant like
   * ".CSV" or ".Csv", it gets normalized to lowercase ".csv".
   * Rejects the edit if another file already uses the same output name.
   * Persists via the context's setOutputFileName which handles the backend PUT.
   */
  function handleCommit(): void {
    let trimmed = editValue.trim()
    if (!trimmed) {
      // Reset to default if cleared
      trimmed = defaultOutputName
    }
    // Normalize existing .csv extension to lowercase, or append if missing
    if (trimmed.toLowerCase().endsWith(".csv")) {
      trimmed = `${trimmed.slice(0, -4)}.csv`
    } else {
      trimmed = `${trimmed}.csv`
    }

    // Attempt to set — context rejects duplicates and returns false
    const success = setOutputFileName(fileKey, trimmed)
    if (!success) {
      setDuplicateError(true)
      return
    }

    setDuplicateError(false)
    setEditValue(trimmed)
    setIsEditing(false)
  }

  /**
   * Handles keyboard events on the inline input.
   * Enter commits, Escape cancels editing.
   *
   * @param e - The keyboard event.
   */
  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === "Enter") {
      handleCommit()
    } else if (e.key === "Escape") {
      setEditValue(currentOutputName)
      setDuplicateError(false)
      setIsEditing(false)
    }
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="flex items-center gap-0.5 w-full">
        <CollapsibleTrigger className="flex items-center gap-1 flex-1 min-w-0 text-left cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5">
          {isOpen ? (
            <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
          )}
          {isEditing ? (
            <Input
              ref={inputRef}
              value={editValue}
              onChange={(e) => { setEditValue(e.target.value); setDuplicateError(false) }}
              onBlur={handleCommit}
              onKeyDown={handleKeyDown}
              onClick={(e) => e.stopPropagation()}
              aria-invalid={duplicateError}
              className="h-5 px-1 py-0 text-[11px] font-mono border-primary/40 focus-visible:ring-1 flex-1 min-w-0"
              aria-label={`Output file name for ${fileName}`}
            />
          ) : (
            <span className="text-[11px] font-medium text-muted-foreground break-words">
              {currentOutputName}{unitLabel && ` (${unitLabel})`}
            </span>
          )}
        </CollapsibleTrigger>

        {/* Action buttons — edit and reset */}
        {!isEditing && (
          <div className="flex items-center shrink-0">
            <button
              onClick={handleEditClick}
              className="p-0.5 rounded hover:bg-muted/50 transition-colors"
              aria-label={`Edit output file name for ${fileName}`}
            >
              <Pencil className="h-2.5 w-2.5 text-muted-foreground" />
            </button>
            <span className="w-[18px] flex justify-center">
              {isCustomName && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleReset}
                      className="p-0.5 rounded hover:bg-muted/50 transition-colors"
                      aria-label={`Reset output file name to ${defaultOutputName}`}
                    >
                      <RotateCcw className="h-2.5 w-2.5 text-muted-foreground" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top">Reset to {defaultOutputName}</TooltipContent>
                </Tooltip>
              )}
            </span>
          </div>
        )}
      </div>
      {duplicateError && (
        <p className="pl-5 text-[10px] text-destructive mt-0.5">
          An output file with this name already exists.
        </p>
      )}
      <CollapsibleContent>
        <div className="pl-4 flex flex-col gap-0.5 mt-0.5">
          {variables.map(v => (
            <VariableSection key={v.sortKey} variable={v} sessionId={sessionId} outputFileName={currentOutputName} surveyId={surveyId} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}

/**
 * Collapsible section for a single survey group.
 * Expands/collapses the file list and their variables underneath.
 *
 * @param group - The survey group containing file→variables hierarchy.
 * @param sessionId - The chat session UUID for persisting collapse state.
 * @returns A collapsible survey section element.
 */
function SurveySection({ group, sessionId }: { group: SurveyGroup; sessionId: string }): React.JSX.Element {
  const nodeKey = `survey:${group.surveyId}`
  const [isOpen, setIsOpen] = usePersistedCollapse(sessionId, nodeKey, true)

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center gap-1 w-full text-left cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5 border-b border-border">
        {isOpen ? (
          <ChevronDown className="h-3 w-3 shrink-0 text-foreground" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0 text-foreground" />
        )}
        <span className="text-xs font-semibold text-foreground">
          {group.surveyName}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pl-2 flex flex-col gap-1 mt-1">
          {Array.from(group.files.entries()).map(([fileName, fileVars]) => (
            <FileSection key={fileName} fileName={fileName} variables={fileVars} sessionId={sessionId} surveyId={group.surveyId} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}


/**
 * Copy-to-clipboard button with a tooltip that shows "Copy" on hover
 * and "Copied!" after clicking. Resets to "Copy" when the tooltip closes.
 *
 * @param text - The string to copy when clicked.
 * @returns A tooltip-wrapped icon button.
 */
function CopyButton({ text }: { text: string }): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const clicking = useRef(false);

  return (
    <Tooltip
      open={open}
      onOpenChange={(isOpen) => {
        // Ignore close events triggered by clicking — we want to stay open.
        if (!isOpen && clicking.current) {
          clicking.current = false;
          return;
        }
        if (!isOpen) {
          setCopied(false);
        }
        setOpen(isOpen);
      }}
    >
      <TooltipTrigger asChild>
        <Button
          data-sidebar="trigger"
          data-slot="sidebar-trigger"
          variant="ghost"
          size="icon-xs"
          onPointerDown={() => { clicking.current = true; }}
          onClick={() => {
            navigator.clipboard.writeText(text);
            setCopied(true);
            setOpen(true);
          }}
        >
          {copied ? <Check /> : <ClipboardCopy />}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">
        {copied ? "Copied!" : "Copy"}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Right-side collapsible sidebar displaying all variables that have been added
 * to the current session's custom extraction. Renders a hierarchical table-like
 * structure grouped by survey, then by source file, with variable names listed
 * alphabetically under each file. Each level (survey, file, variable) is
 * independently collapsible, with state persisted in memory for the duration
 * of the session.
 *
 * Reads from the shared ExtractionsContext (one fetch per session) rather than
 * fetching itself, so adding it alongside many DescribeVariableWidgets doesn't
 * multiply the number of HTTP calls.
 *
 * @param props - Component props.
 * @param props.isOpen - Whether the panel is currently expanded.
 * @param props.onToggle - Callback to toggle the panel.
 * @param props.sessionId - The chat session UUID, used to scope collapse state.
 * @returns The rendered extractions sidebar element.
 */
export function ExtractionsSidebar({
  isOpen,
  onToggle,
  sessionId,
}: ExtractionsSidebarProps): React.JSX.Element {
  // Variables and loading state come from the provider — a single GET feeds
  // both this sidebar and every inline DescribeVariableWidget.
  const { variables, loading, extractionExists, extractionS3Prefix, refresh, surveyInfo, outputFileNames, fileUnitOfAnalysis } = useExtractions()
  const { trackExtraction, isExtractionInProgress } = useExtractionPolling()
  const auth = useAuth()
  const [createError, setCreateError] = useState<string | null>(null)
  // Controls the "clear all" confirmation dialog and tracks the in-flight delete.
  const [isClearDialogOpen, setIsClearDialogOpen] = useState<boolean>(false)
  const [isClearing, setIsClearing] = useState<boolean>(false)
  const [clearError, setClearError] = useState<string | null>(null)
  const sidebarTitle: string = "Custom Extraction"

  // Whether an extraction has already been created for this session.
  // When true, the button reads "Update Extraction" instead of "Create Extraction".
  const hasExistingExtraction: boolean = extractionExists

  // Whether this session currently has an extraction job in flight (tracked globally).
  const isCreating: boolean = isExtractionInProgress(sessionId)

  // Clear collapse memory when the session changes so state starts fresh.
  const prevSessionIdRef = useRef<string>(sessionId)
  useEffect(() => {
    if (prevSessionIdRef.current !== sessionId) {
      clearCollapseStore(prevSessionIdRef.current)
      prevSessionIdRef.current = sessionId
      setCreateError(null)
    }
  }, [sessionId])

  // Memoize the hierarchical grouping so it only recalculates when
  // the variables array reference changes.
  const surveyGroups: SurveyGroup[] = useMemo(
    () => groupVariablesBySurveyAndFile(variables, surveyInfo),
    [variables, surveyInfo]
  )

  // Prune stale keys from the collapse store whenever the variable list changes.
  // This ensures that if a survey or file disappears (all its variables were
  // removed), its stored collapse state is cleaned up. Without this, re-adding
  // a variable to the same survey/file would inherit the old collapsed state.
  useEffect(() => {
    const map = collapseStoreBySession.get(sessionId)
    if (!map || map.size === 0) return

    // Build the set of node keys that currently exist in the data.
    const validKeys = new Set<string>()
    for (const group of surveyGroups) {
      validKeys.add(`survey:${group.surveyId}`)
      for (const [fileName, fileVars] of group.files.entries()) {
        validKeys.add(`file:${group.surveyId}/${fileName}`)
        for (const v of fileVars) {
          validKeys.add(`var:${v.sortKey}`)
        }
      }
    }

    // Remove any stored keys that no longer correspond to existing nodes.
    for (const key of Array.from(map.keys())) {
      if (!validKeys.has(key)) {
        map.delete(key)
      }
    }
  }, [sessionId, surveyGroups])

  /**
   * Handles the "Create Extraction" button click.
   * Submits the extraction job asynchronously — the backend writes an
   * IN_PROGRESS status marker and fires the executor Lambda, then returns
   * 202 immediately. The global BackgroundJobsProvider handles polling
   * and toast notifications regardless of page navigation.
   */
  async function handleCreateExtraction(): Promise<void> {
    if (isCreating || variables.length === 0) return

    setCreateError(null)

    try {
      const accessToken: string | null = await auth.getAccessToken()
      if (!accessToken) {
        throw new Error("Authentication required. Please log in again.")
      }

      // Submit the extraction job (returns 202 immediately — extraction runs async)
      await createExtraction(sessionId, accessToken)

      // Hand off to the global polling provider — it will show toasts and
      // track progress regardless of which page the user navigates to.
      // Pass refresh so the ExtractionsContext updates when the extraction finishes.
      trackExtraction(sessionId, hasExistingExtraction, refresh)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create extraction"
      setCreateError(message)
      toast.error(
        hasExistingExtraction ? "Failed to update extraction." : "Failed to create extraction.",
        { description: message, duration: Infinity }
      )
      console.error("Failed to create extraction:", err)
    }
  }

  /**
   * Handles confirming "Clear all variables".
   *
   * Calls the backend DELETE /api/extractions/{sessionId} endpoint, which
   * removes every variable, survey, and file row for the session, then
   * refreshes the shared context so the panel empties out.
   * @returns A Promise that resolves once the clear completes (or errors).
   */
  async function handleClearAll(): Promise<void> {
    if (isClearing || variables.length === 0) return

    setIsClearing(true)
    setClearError(null)

    try {
      const accessToken: string | null = await auth.getAccessToken()
      if (!accessToken) {
        throw new Error("Authentication required. Please log in again.")
      }

      await clearExtraction(sessionId, accessToken)

      // Drop any persisted collapse state so a fresh extraction starts clean.
      clearCollapseStore(sessionId)

      await refresh()
      setIsClearDialogOpen(false)
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to clear extraction"
      setClearError(message)
      console.error("Failed to clear extraction:", err)
    } finally {
      setIsClearing(false)
    }
  }

  /** Convert an S3 prefix (e.g. "s3://bucket/id/") to the server mount path. */
  function toMountPath(prefix: string): string {
    return `/mnt/s3/${prefix.replace("s3://", "").replace(/\/$/, "")}`
  }

  const fileLocation = toMountPath(extractionS3Prefix)

  return (
    <SecondarySideBar
      width={288}
      side="right"
      isOpen={isOpen}
      onToggle={onToggle}
      sidebarTitle={sidebarTitle}
    >
        <div
          className="flex flex-col min-h-0 flex-1 overflow-hidden"
        >
          {/* Scrollable hierarchical variable list */}
          <div className="flex-1 overflow-y-auto p-3">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : variables.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-8">
              No variables added yet. Use &quot;Add To Extraction&quot; on a described variable to
              start building your extraction.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {surveyGroups.map(group => (
                <SurveySection key={group.surveyId} group={group} sessionId={sessionId} />
              ))}
            </div>
          )}
        </div>

        {/* Extraction location — pinned above the button */}
        {hasExistingExtraction && (
          <div className="px-3 py-2 border-t shrink-0">
            <p className="text-xs text-muted-foreground">
              Extraction at: <span className="font-mono">{fileLocation}
              <CopyButton text={fileLocation} />
            </span>
            </p>
          </div>
        )}

        {/* Fixed bottom buttons */}
        <div className="p-3 border-t shrink-0">
          {createError && (
            <p className="text-xs text-destructive mb-2 max-h-10 overflow-y-auto">{createError}</p>
          )}
          <Button
            className="w-full"
            disabled={variables.length === 0 || isCreating}
            onClick={handleCreateExtraction}
          >
            {isCreating ? (
                <LoadingSpinner loadingContent={hasExistingExtraction ? "Updating..." : "Creating..."} />
            ) : (
              hasExistingExtraction ? "Update Extraction" : "Create Extraction"
            )}
          </Button>
          {/* Secondary actions share one row so the footer stays two rows tall. */}
          <div className="flex gap-2 mt-2">
            <Button
              variant="outline"
              className="flex-1 min-w-0 px-3 text-xs"
              disabled={variables.length === 0}
              onClick={() =>
                exportExtractionAsJson({
                  sessionId,
                  surveyGroups,
                  extractionExists,
                  extractionS3Prefix,
                  outputFileNames,
                  surveyInfoMap: surveyInfo,
                  fileUnitOfAnalysis,
                })
              }
            >
              <Download className="h-3.5 w-3.5 shrink-0" />
              Export as JSON
            </Button>
            <Button
              variant="outline"
              className="flex-1 min-w-0 px-3 text-xs text-destructive hover:text-destructive"
              disabled={variables.length === 0 || isCreating || isClearing}
              onClick={() => {
                setClearError(null)
                setIsClearDialogOpen(true)
              }}
            >
              <Trash2 className="h-3.5 w-3.5 shrink-0" />
              Clear All
            </Button>
          </div>
        </div>
      </div>

      {/* Confirmation dialog for clearing the entire extraction. */}
      <Dialog
        open={isClearDialogOpen}
        onOpenChange={(open) => {
          // Don't allow dismissing mid-delete so the user isn't left unsure
          // whether the clear ran.
          if (isClearing) return
          setIsClearDialogOpen(open)
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Clear all variables?</DialogTitle>
            <DialogDescription>
              This removes every variable and their overrides from this session&apos;s custom extraction.
              This can&apos;t be undone, but you can add variables again afterwards.
            </DialogDescription>
          </DialogHeader>
          {clearError && (
            <p className="text-xs text-destructive">{clearError}</p>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsClearDialogOpen(false)}
              disabled={isClearing}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleClearAll}
              disabled={isClearing}
            >
              {isClearing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Clearing...
                </>
              ) : (
                "Clear All"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SecondarySideBar>
  )
}
